import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MemberInsurerServiceService } from '../modules/lps-member-login/services/member-insurer-service.service';
import { Roles } from '../modules/lps-member-login/models/commonmodel';

@Injectable({
  providedIn: 'root'
})
export class TokenService {

  constructor(
    private memberService: MemberInsurerServiceService,
    private router: Router
  ) {}

  /**
   * Get the current authentication token
   */
  getToken(): string | null {
    const userInfo = this.memberService.getLoginUserInfo();
    return userInfo?.token || null;
  }

  /**
   * Check if the user has a valid token
   */
  hasValidToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired
    const userInfo = this.memberService.getLoginUserInfo();
    if (userInfo?.tokenExpiresAt) {
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      const tokenExpiry = Math.floor(userInfo.tokenExpiresAt / 1000) +10000; // Convert milliseconds to seconds
      return currentTime < tokenExpiry;
    }

    return true; // If no expiration time, assume valid
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const userInfo = this.memberService.getLoginUserInfo();
    if (!userInfo?.tokenExpiresAt) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    const tokenExpiry = Math.floor(userInfo.tokenExpiresAt / 1000) ; // Convert milliseconds to seconds
    return currentTime >= tokenExpiry;
  }

  /**
   * Get time until token expires (in seconds)
   */
  getTimeUntilExpiry(): number {
    const userInfo = this.memberService.getLoginUserInfo();
    if (!userInfo?.tokenExpiresAt) return 0;

    const currentTime = Math.floor(Date.now() / 1000);
    const tokenExpiry = Math.floor(userInfo.tokenExpiresAt / 1000); // Convert milliseconds to seconds
    return Math.max(0, tokenExpiry - currentTime);
  }

  /**
   * Clear the current token and user session
   */
  clearToken(): void {
    this.memberService.clearLoginUserRole();
  }

  /**
   * Perform automatic logout and redirect based on user role
   */
  autoLogout(): void {
    const role = this.memberService.getLoginUserRole();
    const userAccess = this.memberService.getUserAccess();

    // Clear the session first
    this.memberService.clearLoginUserRole();

    // Redirect based on role and user access (same logic as header component)
    if (role === Roles.INSURER) {
      if (userAccess === Roles.ADMIN_INSURER) {
        this.router.navigate(['/login/adminInsurer']);
      } else {
        this.router.navigate(['/login/insurer']);
      }
    } else if (role === Roles.DISTRIBUTOR) {
      if (userAccess === Roles.ADMIN_DISTRIBUTOR) {
        this.router.navigate(['/login/distributorAdmin']);
      } else {
        this.router.navigate(['/login/distributor']);
      }
    } else if (role === Roles.SUPER_ADMIN) {
      this.router.navigate(['/login/superadmin']);
    } else {
      if (userAccess === Roles.ADMIN_PROVIDER) {
        this.router.navigate(['/login/adminProvider']);
      } else {
        this.router.navigate(['/login/provider']);
      }
    }

    console.warn('Automatic logout due to token expiration. User redirected to appropriate login page.');
  }

  /**
   * Check if token is about to expire (within 5 minutes)
   */
  isTokenExpiringSoon(): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    return timeUntilExpiry > 0 && timeUntilExpiry <= 300; // 5 minutes = 300 seconds
  }

  /**
   * Start monitoring token expiration
   */
  startTokenMonitoring(): void {
    // Check token every minute
    setInterval(() => {
      if (this.isTokenExpired()) {
        this.autoLogout();
      } else if (this.isTokenExpiringSoon()) {
        console.warn(`Token expires in ${this.getTimeUntilExpiry()} seconds`);
      }
    }, 60000); // Check every minute
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.memberService.isUserLoggedIn() === 'true' && this.hasValidToken();
  }
}
